import * as React from 'react'
import { WPSClient, APIClient, RequestContext, VideoWizardTitle, VideoWizardViewModel, safeBool, pushState } from 'DVWebNodeUtils'
import { Grid } from '@amzn/dvui/dist/Grid'
import { Packshot } from '@amzn/dvui/dist/Packshot'
import { PrimeButton } from '@amzn/dvui/dist/Button'
import { Args } from '@utils'
import * as styles from './VideoWizard.module.scss'
import * as cn from 'classnames'
import { PrimaryButton } from '@amzn/dvui/dist/Button';

interface HeaderProps extends VideoWizardViewModel {
  selectionCount: number
  closeHandler?: () => void
  stickyRef?: React.RefObject<HTMLDivElement>
  isStickyHeader?: boolean
}

enum ModalState {
  INVISIBLE,
  STOREFRONT_INGRESS,
  STICKY_FOOTER,
  SELECTION_GRID,
  EDUCATIONAL_SCREEN
}

interface VideoWizardState {
  titleList: VideoWizardTitle[]
  selectedTitleIds: Set<string>
  modalState: ModalState
  header?: HeaderProps
  buttonText?: string
  minTitlesToSelect?: number
  forcedWaitDuration?: number
  optInText?: string
  optOutText?: string
  showMeMoreText?: string
  token?: string
  treatmentModalState?: ModalState
  submitted?: boolean
}

export interface VideoWizardProps extends Args {
  context: RequestContext
  storefrontReloadPageHandler?: () => void
  onComplete: () => void
  onShow: () => void
}

interface FloatingButtonContainerProps {
  label: string
  actionHandler: () => void
  submittable: boolean
}

interface SelectionScreenProps {
  header: HeaderProps
  selectedTitleIds: Set<string>
  minTitlesToSelect: number
  titleList: VideoWizardTitle[]
  toggleHandler: (titleId: string) => (e: React.MouseEvent) => void
  submitHandler: () => void
  closeHandler: () => void
}

interface SelectionScreenState {
  isStickyHeader: boolean
}

interface EducationalScreenProps extends HeaderProps {
  closeEducationalPageHandler: Function
}

interface StickyFooterProps {
  footer: {
    heading: HeaderProps
    optInText: string
    optOutText: string
  }
  showVideoWizardHandler: (selectedTitleIds: Set<string>) => () => void
  closeFooterHandler: () => void
}

interface StorefrontIngressProps {
  ingress: {
    heading: HeaderProps
    titleList: VideoWizardTitle[]
    showMeMoreText: string
  }
  showVideoWizardHandler: (selectedTitleIds: Set<string>) => () => void
}

interface StorefrontIngressState {
  selectedTitleIds: Set<string>
}

interface IngressPacshotTitle {
  title: VideoWizardTitle
  selected: boolean
}
interface IngressPackshotProps {
  packshotTitles: IngressPacshotTitle[]
  showVideoWizardHandler: (selectedTitleIds: Set<string>) => () => void
}

const SELECTION_PLACEHOLDER = '{count}'

const VIDEO_WIZARD_BUTTON_ID = 'VideoWizardButton'

const VideoWizardPrimeLogo = () => <div className={styles.PrimeLogo}></div>

const Header = (props: HeaderProps) => <div className={styles.VideoWizardHeader}>
  <VideoWizardPrimeLogo/>
  <div className={styles.VideoWizardHeaderHeadingText}>
    {props.headingText}
  </div>
  <div className={styles.VideoWizardHeaderBodyText}>{props.bodyText}</div>
  <div className={styles.VideoWizardHeaderSubheading} ref={props.stickyRef}>
      <div className={cn(styles.VideoWizardHeaderSubheadingContentContainer,{
        [styles.Fixed]: props.isStickyHeader
      })}>
      {props.subheadingText}
      <span className={cn(styles.VideoWizardSelection, (props.selectionCount >= props.minTitlesToSelect ?
        styles.VideoWizardSelectionActive : ''))}>
        {props.selectionText.replace(SELECTION_PLACEHOLDER, props.selectionCount.toString())}
      </span>
    </div>
  </div>
</div>

class FloatingButtonContainer extends React.Component<FloatingButtonContainerProps> {

  constructor(props: FloatingButtonContainerProps) {
    super(props)
  }

  render() {
    return (<div
      id={VIDEO_WIZARD_BUTTON_ID}
      className={cn(styles.VideoWizardFloatingButton, styles.VideoWizardButtonFixed)}>
        <div className={styles.VideoWizardFloatingButtonContentContainer}>
          <PrimeButton className={cn({
            [styles.VideoWizardSubmitButton]: true,
            [styles.VideoWizardSubmitButtonSubmittable]: this.props.submittable,
            [styles.VideoWizardSubmitButtonSkip]: !this.props.submittable
          })}
            onClick={this.props.actionHandler}>
            {this.props.label}
          </PrimeButton>
        </div>
    </div>)
  }
}

class EducationalScreen extends React.Component<EducationalScreenProps> {

  constructor(props: EducationalScreenProps) {
    super(props)
  }

  componentDidMount() {
    setTimeout(() => {
      this.props.closeEducationalPageHandler()
    }, this.props.forcedWaitDuration)
  }

  render() {
    return (
      <div
        className={styles.VideoWizardEducationalContainer}>
        <div className={styles.VideoWizardEducationalSpinner} />
        <div className={styles.VideoWizardEducationalLoadingText}>{this.props.loadingText}</div>
      </div>
    )
  }
}

class SelectionScreen extends React.Component<SelectionScreenProps, SelectionScreenState> {
  io: IntersectionObserver
  headerRef: React.RefObject<HTMLDivElement>

  constructor(props: SelectionScreenProps) {
    super(props)
    this.headerRef = React.createRef()
    this.state = {
      isStickyHeader: false
    }
  }

  componentDidMount() {
    this.io = new IntersectionObserver((entries) => {
      entries.forEach(entry => this.setState({
          isStickyHeader: entry.intersectionRatio !== 1
      }))
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0
    })

    this.io.observe(this.headerRef.current)
  }

  componentWillUnmount() {
    this.io.disconnect()
  }

  render() {
    const { header, selectedTitleIds, titleList, minTitlesToSelect,
            toggleHandler, submitHandler, closeHandler } = this.props
    const submittable = selectedTitleIds.size >= minTitlesToSelect
    return (
      <div className={styles.VideoWizardSelectionContainer}>
        <div className={styles.VideoWizardScrollableArea}>
            { header && <Header {...header}
                                closeHandler={closeHandler}
                                stickyRef={this.headerRef}
                                isStickyHeader={this.state.isStickyHeader}/>}
          <div className={styles.VideoWizardGridContainer}>
            <Grid className={styles.VideoWizardGrid}>
              {titleList.map((title) =>
                <Packshot
                  className={styles.VideoWizardPackshot}
                  key={`title-${title.id}`}
                  src={title.image}
                  alt={title.title}
                  overlay={selectedTitleIds.has(title.id) &&
                    <div className={styles.VideoWizardSelectedOverlay}
                      onClick={toggleHandler(title.id)}>
                      <div className={styles.VideoWizardOverlayThumbsUp}/>
                    </div>}
                  onClick={toggleHandler(title.id)}/>
                )}
            </Grid>

            <FloatingButtonContainer
              label={submittable ? header.submitText : header.skipText}
              submittable={submittable}
              actionHandler={submittable ? submitHandler : closeHandler}
            />
          </div>
        </div>
      </div>
    )
  }
}

const StickyFooter = (props: StickyFooterProps) => {
  const { heading, optInText, optOutText } = props.footer
  return (
    <div className={styles.VideoWizardStickyFooter}>
      <div className={styles.VideoWizardStickyFooterContentContainer}>
        <div className={styles.VideoWizardStickyFooterPopcorn}></div>
        <div className={styles.VideoWizardStickyFooterMessage}>
          <div className={styles.VideoWizardStickyFooterMessageHeading}>
            {heading.headingText}
          </div>
          <div className={styles.VideoWizardStickyFooterMessageBody}>
            {heading.bodyText}
          </div>
        </div>
        <div className={styles.VideoWizardStickyFooterButtons}>
          <PrimeButton onClick={props.showVideoWizardHandler(new Set())}>{optInText} </PrimeButton>
          <PrimaryButton className={styles.VideoWizardStickyFooterBtnNow}
            onClick={props.closeFooterHandler}>{optOutText}</PrimaryButton>
        </div>
      </div>
    </div>
  )
}

const IngressPackshots = (props: IngressPackshotProps) => <div className={styles.VideoWizardIngressPackshotCollection}>
  {props.packshotTitles.map(packshotTitle =>
    <div key={packshotTitle.title.id} onClick={props.showVideoWizardHandler(new Set([packshotTitle.title.id]))}>
      <Packshot key={`${packshotTitle.title.id}`} className={styles.VideoWizardIngressPackshot}
        src={packshotTitle.title.image}
        overlay={<div className={styles.VideoWizardIngressThumbsUp}/>}
      />
    </div>
  )}
</div>

class StorefrontIngress extends React.Component<StorefrontIngressProps, StorefrontIngressState> {
  constructor(props: StorefrontIngressProps) {
    super(props)
    this.state = {
      selectedTitleIds: new Set()
    }
  }

  render() {
    const CHUNK_SIZE = 3
    const { heading, titleList, showMeMoreText } = this.props.ingress
    const packshotTitles = titleList.slice(0, CHUNK_SIZE)
                                    .map(title => ({
                                      title: title,
                                      selected: this.state.selectedTitleIds.has(title.id)
                                    }))

    return (
      <div className={styles.VideoWizardIngress}>
        <div className={styles.VideoWizardIngressContentContainer}>
          <div className={styles.VideoWizardIngressPopcorn}/>
          <div className={styles.VideoWizardIngressHeadingText}>
            {heading.headingText}
          </div>
          <div className={styles.VideoWizardIngressBodyText}>
            {heading.bodyText}
          </div>
          <IngressPackshots packshotTitles={packshotTitles}
            showVideoWizardHandler={this.props.showVideoWizardHandler}/>
          <div className={styles.VideoWizardIngressShowMe}
            onClick={this.props.showVideoWizardHandler(new Set())}>
            {showMeMoreText}
          </div>
        </div>
      </div>
    )
  }
}

export class VideoWizard extends React.Component<VideoWizardProps & Args, VideoWizardState> {
  wpsClient: WPSClient
  constructor(props: VideoWizardProps) {
    super(props)
    this.wpsClient = new WPSClient(new APIClient)

    this.state = {
      selectedTitleIds: new Set(),
      titleList: [],
      modalState: ModalState.INVISIBLE
    }
  }

  private fetchData() {
    this.wpsClient.VideoWizard({context: this.props.context}).then((videoWizardModel) => {
      if (videoWizardModel &&
          safeBool(videoWizardModel.isEligible) &&
          videoWizardModel.viewModel) {

        const treatmentStateMapping: {[treatment: string] : ModalState} = {
          "C" : ModalState.INVISIBLE,
          "T1" : ModalState.STICKY_FOOTER,
          "T2" : ModalState.STOREFRONT_INGRESS
        }
        // Always one of three treatments
        const initialModalState = treatmentStateMapping[videoWizardModel.viewModel.ingressTreatment]
        // TODO: https://sim.amazon.com/issues/PVWebXPL-1178
        if (initialModalState  !== ModalState.INVISIBLE) {
          this.props.logger.count(`DVWeb::VideoWizard::Shown::ModalState-${initialModalState}`, 1, false)
          window.addEventListener('popstate', this.onPopState)
          pushState({ modalState: initialModalState }, '', window.location.href)
        }

        this.setState({
          titleList: videoWizardModel.viewModel.titleList,
          selectedTitleIds: new Set(),
          modalState: initialModalState,
          header: {
            ...videoWizardModel.viewModel,
            selectionCount: 0
          },
          buttonText: videoWizardModel.viewModel.submitText,
          minTitlesToSelect: videoWizardModel.viewModel.minTitlesToSelect,
          forcedWaitDuration: videoWizardModel.viewModel.forcedWaitDuration,
          optInText: videoWizardModel.viewModel.optInText,
          optOutText: videoWizardModel.viewModel.optOutText,
          showMeMoreText: videoWizardModel.viewModel.showMeMoreText,
          token: videoWizardModel.viewModel.token,
          treatmentModalState: initialModalState,
          submitted: false
        })
      } else {
        this.setState({ modalState: ModalState.INVISIBLE })
        this.props.onComplete()
      }
    })
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onPopState)
  }

  componentDidMount() {
    this.fetchData()
  }

  componentDidCatch(error: Error) {
    window.removeEventListener('popstate', this.onPopState)
    this.onModalClose()
    this.props.logger.count('DVWeb::VideoWizard::JSFatal', 1, true)
    this.props.logger.error(error, '[DVWeb::VideoWizard] Error while rendering VideoWizard')
    this.props.onComplete()
  }

  onPopState = (event: PopStateEvent) => {
    if (!this.state.submitted &&
        event && event.state) {
      if (event.state.modalState === ModalState.SELECTION_GRID) {
        this.setState({
          modalState: ModalState.SELECTION_GRID
        })
        this.props.onShow()
      } else if (event.state.modalState !== ModalState.INVISIBLE) {
        this.setState({
          modalState: this.state.treatmentModalState
        })
        this.props.onComplete()
      }
    }
  }

  toggleSelect = (titleId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const currentSelectedTitleIds = this.state.selectedTitleIds
    if (currentSelectedTitleIds.has(titleId)) {
      currentSelectedTitleIds.delete(titleId)
    } else {
      currentSelectedTitleIds.add(titleId)
    }

    this.setState({
      selectedTitleIds: currentSelectedTitleIds,
      header: {
        ...this.state.header,
        selectionCount: currentSelectedTitleIds.size
      }
    })
  }

  onModalClose = () => {
    this.setState({ modalState: ModalState.INVISIBLE })
    this.props.onComplete()
  }

  skipModal = (fromIngress: boolean) => () => {
    this.onModalClose()
    // TODO: https://sim.amazon.com/issues/PVWebXPL-1178
    if (this.state.treatmentModalState) {
      this.props.logger.count(`DVWeb::VideoWizard::ModalSkipped::ModalState-${this.state.treatmentModalState}`, 1, false)
    }
    if (fromIngress) {
      this.props.logger.count('DVWeb::VideoWizard::FooterIngressSkipped', 1, false)
    } else {
      this.props.logger.count('DVWeb::VideoWizard::GridSkipped', 1, false)
    }

    if (this.state.token) {
      this.wpsClient.SubmitVideoWizard({
        context: this.props.context,
        titleIds: Array.from(this.state.selectedTitleIds),
        hasSkipped: true,
        token: this.state.token
      })
    }
  }

  submitSelection = async () => {
    if (this.state.treatmentModalState) {
      // TODO: https://sim.amazon.com/issues/PVWebXPL-1178
      this.props.logger.count(`DVWeb::VideoWizard::Submitted::ModalState-${this.state.treatmentModalState}`, 1, false)
    }
    const params = {
      context: this.props.context,
      titleIds: Array.from(this.state.selectedTitleIds),
      hasSkipped: false,
      token: this.state.token
    }
    this.setState({
      modalState: ModalState.EDUCATIONAL_SCREEN,
      submitted: true
    })

    if (this.state.token) {
      this.wpsClient.SubmitVideoWizard(params)
    }
  }

  closeEducationalPageHandler = () => {
    window.scrollTo(0, 0)
    this.onModalClose()
    this.props.storefrontReloadPageHandler()
  }

  showVideoWizardHandler = (selectedTitleIds: Set<string> = new Set()) => () => {
    // TODO: https://sim.amazon.com/issues/PVWebXPL-1178
    if (this.state.treatmentModalState) {
      this.props.logger.count(`DVWeb::VideoWizard::GridShown::ModalState-${this.state.treatmentModalState}`, 1, false)
    }
    pushState({ modalState: ModalState.SELECTION_GRID }, '', window.location.href)
    this.setState({
      modalState: ModalState.SELECTION_GRID,
      selectedTitleIds: selectedTitleIds,
      header: {
        ...this.state.header,
        selectionCount: selectedTitleIds.size
      }
    })

    this.props.onShow()
  }

  render() {
    const wrapInContainer = (component: JSX.Element) => <div className={styles.VideoWizardContainer}>
      {component}
    </div>

    switch (this.state.modalState) {
      case ModalState.STICKY_FOOTER: {
        const footer = {
          heading: this.state.header,
          optInText: this.state.optInText,
          optOutText: this.state.optOutText
        }
        return <StickyFooter
          footer={footer}
          showVideoWizardHandler={this.showVideoWizardHandler}
          closeFooterHandler={this.skipModal(true)}
        />
      }
      case ModalState.STOREFRONT_INGRESS: {
        const ingress = {
          heading: this.state.header,
          titleList: this.state.titleList,
          showMeMoreText: this.state.showMeMoreText
        }
        return <StorefrontIngress
          ingress={ingress}
          showVideoWizardHandler={this.showVideoWizardHandler}
        />
      }
      case ModalState.SELECTION_GRID: {
        const props = {
          header: this.state.header,
          selectedTitleIds: this.state.selectedTitleIds,
          minTitlesToSelect: this.state.minTitlesToSelect,
          titleList: this.state.titleList
        }
        return wrapInContainer(<SelectionScreen {...props}
          toggleHandler={this.toggleSelect}
          submitHandler={this.submitSelection}
          closeHandler={this.skipModal(false)}
        />)
      }
      case ModalState.EDUCATIONAL_SCREEN:
        return wrapInContainer(<EducationalScreen {...this.state.header}
          closeEducationalPageHandler={this.closeEducationalPageHandler.bind(this)}
      />)
      default: return (null)
    }
  }
}
